/**
 * Reddit Ads Adapter
 *
 * Adapts event data into Reddit Pixel format.
 * Returns unified data format that works for both browser pixel (rdt()) and CAPI.
 *
 * Documentation:
 * - https://business.reddithelp.com/s/article/Install-the-Reddit-Pixel-on-your-website
 * - https://business.reddithelp.com/s/article/conversion-events-in-the-event-setup-tool
 * - https://business.reddithelp.com/s/article/manual-conversion-events-with-the-reddit-pixel
 * - https://ads-api.reddit.com/docs/v3/capi-direct-integration
 *
 * @since 1.51.0
 */

(function (pmw, undefined) {

	class RedditAdapter {

		/**
		 * Adapt event data to Reddit Pixel format
		 *
		 * @param {object} eventData - Event-specific data (product, order, etc.)
		 * @param {string} eventName - Standardized event name
		 * @param {object} context - Event context (user, page, etc.)
		 * @returns {object|null} Adapted data for Reddit or null if pixel not configured
		 */
		adapt(eventData, eventName, context) {

			// Check if Reddit pixel is configured (has advertiser_id)
			// Don't check 'loaded' status - pixel might not be loaded yet when event fires
			// The event listener will check canNotFireRedditEvent() before actually firing
			if (!pmwDataLayer?.pixels?.reddit?.advertiser_id) {
				return null;
			}
			const eventMap = {
				"page_view"      : () => this.adaptPageVisit(eventData),
				"add_to_cart"    : () => this.adaptAddToCart(eventData),
				"view_item"      : () => this.adaptViewContent(eventData),
				"search"         : () => this.adaptSearch(eventData),
				"add_to_wishlist": () => this.adaptAddToWishlist(eventData),
				"view_category"  : () => this.adaptViewCategory(eventData),
				"purchase"       : () => this.adaptPurchase(eventData),
			};

			if (!eventMap[eventName]) {
				return null;
			}

			return this.clampMetrics(eventMap[eventName]());
		}

		/**
		 * Clamp item counts and values to realistic bounds.
		 *
		 * Reddit's CAPI rejects out-of-range item counts and values
		 * ("invalid value on field itemCount"). Bot/abuse traffic or unusual
		 * WooCommerce configurations can inflate cart quantities to absurd
		 * numbers, which would get the whole event rejected. Clamping a single
		 * poisoned field keeps the rest of the event deliverable.
		 *
		 * @since 1.59.0
		 *
		 * @param {object|null} data - Adapted Reddit event data.
		 * @returns {object|null} The data with counts/values clamped.
		 */
		clampMetrics(data) {
			if (!data || typeof data !== "object") {
				return data;
			}

			const MAX_ITEM_COUNT = 100000;
			const MAX_VALUE       = 1000000000;

			const clampCount = value => {
				const n = Number(value);
				return Number.isFinite(n) ? Math.min(MAX_ITEM_COUNT, Math.max(1, Math.round(n))) : 1;
			};

			const clampValue = value => {
				const v = Number(value);
				if (!Number.isFinite(v) || v < 0) return 0;
				return Math.min(MAX_VALUE, v);
			};

			for (const bucket of [data.event_data, data.event_metadata, data.metadata]) {
				if (!bucket || typeof bucket !== "object") {
					continue;
				}
				if (bucket.itemCount !== undefined && bucket.itemCount !== null) {
					bucket.itemCount = clampCount(bucket.itemCount);
				}
				if (bucket.item_count !== undefined && bucket.item_count !== null) {
					bucket.item_count = clampCount(bucket.item_count);
				}
				if (bucket.value !== undefined && bucket.value !== null) {
					bucket.value = clampValue(bucket.value);
				}
			}

			return data;
		}

		/**
		 * Get base Reddit S2S event data structure
		 *
		 * @returns {object} Base event data
		 */
		getBaseData() {

			// The visitor UUID, click ID and user data the Conversions API
			// needs come from functions_premium.js, which the free build does
			// not ship. The browser pixel only reads event_name and event_data.
			if (typeof pmw.getRedditS2SBaseData === "function") {
				return pmw.getRedditS2SBaseData();
			}

			return {
				event_at     : Date.now(),
				action_source: "WEBSITE",
				user         : {},
			};
		}

		/**
		 * Adapt add_to_cart event
		 * Returns unified format for both browser pixel (rdt) and CAPI
		 */
		adaptAddToCart(eventData) {
			if (!eventData?.product) return null;

			const product = eventData.product;
			const eventId = pmw.getRandomEventId();

			// Return unified data structure for both browser and server-side
			const data      = this.getBaseData();
			data.event_id   = eventId;
			data.event_name = "AddToCart"; // For browser pixel
			data.event_type = {
				tracking_type: "ADD_TO_CART", // For CAPI v3 (UPPER_SNAKE_CASE)
			};
			data.type = data.event_type; // CAPI v3 field name

			const productCategory = Array.isArray(product.category) ? product.category.join(", ") : product.category;

			// Browser pixel event data
			data.event_data = {
				currency    : pmw.getProductCurrency(product),
				itemCount   : product.quantity,
				value       : product.price * product.quantity,
				conversionId: eventId, // For browser pixel deduplication
				products    : [{
					id      : pmw.getProductIdForSpecificPixel(product, "reddit"),
					category: productCategory,
					name    : product.name,
				}],
			};

			// CAPI event metadata (output both field names for backwards compat)
			data.event_metadata = {
				item_count   : product.quantity || 1,
				currency     : pmw.getProductCurrency(product),
				value        : parseFloat(product.price * product.quantity),
				conversion_id: eventId,
				products     : [{
					id      : pmw.getProductIdForSpecificPixel(product, "reddit"),
					category: productCategory,
					name    : product.name,
				}],
			};
			data.metadata = data.event_metadata; // CAPI v3 field name

			return data;
		}

		/**
		 * Adapt page_view event (PageVisit in Reddit)
		 */
		adaptPageVisit(eventData) {
			const eventId = pmw.getRandomEventId();

			const data      = this.getBaseData();
			data.event_id   = eventId;
			data.event_name = "PageVisit";
			data.event_type = {
				tracking_type: "PAGE_VISIT", // For CAPI v3 (UPPER_SNAKE_CASE)
			};
			data.type = data.event_type; // CAPI v3 field name

			data.event_data = {
				conversionId: eventId,
			};

			data.event_metadata = {
				conversion_id: eventId,
			};
			data.metadata = data.event_metadata; // CAPI v3 field name

			return data;
		}

		/**
		 * Adapt view_content event
		 */
		adaptViewContent(eventData) {
			const eventId = pmw.getRandomEventId();

			const data      = this.getBaseData();
			data.event_id   = eventId;
			data.event_name = "ViewContent";
			data.event_type = {
				tracking_type: "VIEW_CONTENT", // For CAPI v3 (UPPER_SNAKE_CASE)
			};
			data.type = data.event_type; // CAPI v3 field name

			data.event_data = {
				conversionId: eventId,
			};

			data.event_metadata = {
				conversion_id: eventId,
			};
			data.metadata = data.event_metadata; // CAPI v3 field name

			return data;
		}

		/**
		 * Adapt search event
		 */
		 adaptSearch(eventData) {
			const eventId = pmw.getRandomEventId();

			const data      = this.getBaseData();
			data.event_id   = eventId;
			data.event_name = "Search";
			data.event_type = {
				tracking_type: "SEARCH", // For CAPI v3 (UPPER_SNAKE_CASE)
			};
			data.type = data.event_type; // CAPI v3 field name

			data.event_data = {
				conversionId: eventId,
			};

			data.event_metadata = {
				conversion_id: eventId,
			};
			data.metadata = data.event_metadata; // CAPI v3 field name

			return data;
		}

		/**
		 * Adapt add_to_wishlist event
		 */
		adaptAddToWishlist(eventData) {
			if (!eventData?.product) return null;

			const product = eventData.product;
			const eventId = pmw.getRandomEventId();

			const data      = this.getBaseData();
			data.event_id   = eventId;
			data.event_name = "AddToWishlist";
			data.event_type = {
				tracking_type: "ADD_TO_WISHLIST", // For CAPI v3 (UPPER_SNAKE_CASE)
			};
			data.type = data.event_type; // CAPI v3 field name

			data.event_data = {
				currency    : pmw.getProductCurrency(product),
				itemCount   : product.quantity,
				value       : product.price * product.quantity,
				conversionId: eventId,
			};

			data.event_metadata = {
				item_count   : product.quantity || 1,
				currency     : pmw.getProductCurrency(product),
				value        : parseFloat(product.price * product.quantity),
				conversion_id: eventId,
			};
			data.metadata = data.event_metadata; // CAPI v3 field name

			return data;
		}

		/**
		 * Adapt view_category event
		 *
		 * Note: Reddit doesn't have a specific ViewCategory event.
		 * Return null as this event is not supported.
		 *
		 * @param {object} eventData - Event data
		 * @returns {null} Reddit doesn't support view category event
		 */
		adaptViewCategory(eventData) {
			// Reddit doesn't have a ViewCategory event type
			return null;
		}

		/**
		 * Adapt purchase event
		 */
		adaptPurchase(eventData) {
			// For purchase, use order ID as event_id for deduplication
			const eventId = pmwDataLayer.order.id ? "pmw_" + pmwDataLayer.order.id.toString() : pmw.getRandomEventId();

			const data = {
				event_id  : eventId,
				event_name: "Purchase",
				event_data: {
					conversionId: "pmw_" + pmwDataLayer.order.number,
					currency    : pmwDataLayer.order.currency,
					value       : pmwDataLayer.order.value.marketing,
					products    : pmw.redditGetProductsFromCart(),
				},
			};

			// Get order item count
			if (pmwDataLayer.order.items) {
				let itemCount = 0;
				pmwDataLayer.order.items.forEach(item => {
					itemCount += parseInt(item.quantity);
				});

				data.event_data.itemCount = itemCount;
			}

			return data;
		}
	}

	// Register the adapter with S2S support
	pmw.registerAdapter("reddit", new RedditAdapter(), {
		supportsS2S               : true,
		supportsEventDeduplication: false,
		supportsPageViewS2S       : true,
		isActive                  : () => pmwDataLayer?.pixels?.reddit?.loaded === true && pmw.consent.categories.get().marketing === true,
		isConfigured              : () => !!pmwDataLayer?.pixels?.reddit?.advertiser_id,
	});

}(window.pmw = window.pmw || {}));
